<template>
    <div class="appeals-table">
        <div class="d-flex search">
            <v-text-field v-model="searchString" class="appeals-table__search" solo
                :placeholder="$t('appeals.placeholder')" />
            <v-btn type="button" class="search-btn warning" depressed large @click="filters.search = searchString">
                {{ $t('common.btns.search') }}
            </v-btn>
        </div>

        <v-flex d-flex>
            <v-menu ref="menuDate" :close-on-content-click="false" v-model="menuDate" :return-value.sync="filters.dates"
                max-width="280px" min-width="280px" class="pr-4 mt-2" transition="scale-transition">
                <template v-slot:activator="{ on }">
                    <v-text-field v-on="on" v-model="filters.dates" prepend-icon="mdi-calendar"
                        :label="$t('common.fields.rentDate')" readonly class="appeals-table__filter" />
                </template>
                <v-date-picker v-model="filters.dates" color="primary lighten-1" width="280" range :locale="$i18n.locale"
                    :selected-items-text="filters.dates.toString()" :allowed-dates="allowedDates"
                    @input="changeDate"></v-date-picker>
            </v-menu>

            <v-select v-model="filters.status" class="appeals-table__filter" item-text="name" item-value="id"
                :items="statuses" :label="$t('common.fields.status')" :loading="statusesLoading" clearable />

            <v-select v-model="filters.category" class="appeals-table__filter" item-text="name" item-value="id"
                :items="categories" :label="$t('appeals.category')" :loading="categoriesLoading" clearable />
        </v-flex>

        <v-data-table class="mt-5" :headers="headers" :items="list" :loading="loading" :items-per-page="perPage"
            hide-default-footer>
            <template #item="{ item }">
                <tr :class="{ updating: itemsUpdating.includes(item.id) }">
                    <td>
                        <router-link class="appeal-link" :to="!$can('can_reply_appeal') ? '' : `/appeals/${item.id}/info`">
                            {{ item.num }}
                        </router-link>
                    </td>
                    <td>
                        <router-link class="appeal-link" :to="!$can('can_reply_appeal') ? '' : `/appeals/${item.id}/info`">
                            {{ item.created_at }}
                        </router-link>
                    </td>
                    <td>
                        <router-link class="appeal-link" :to="!$can('can_reply_appeal') ? '' : `/appeals/${item.id}/info`">
                            {{ item.car ? item.car.number : '' }}
                        </router-link>
                    </td>
                    <td>{{ item.investor && item.investor.user ? item.investor.user.name : '' }}</td>

                    <td>
                        <template v-if="noEdit">
                            {{ item.responsible ? item.responsible.name : '-' }}
                        </template>

                        <v-select v-else class="responsible-select" item-text="name" item-value="id" dense hide-details
                            :items="employees" :value="item.responsible_id" :disabled="!$can('appeals_change_responsible')"
                            @change="onItemUpdate(item.id, { responsible_id: $event })" />
                    </td>
                    <td>
                        <template v-if="noEdit">
                            {{ getCategoryName(item.category) }}
                        </template>

                        <v-select v-else class="category-select" item-text="name" item-value="id" dense hide-details
                            :items="categories" :value="item.category"
                            :disabled="!$can('appeals_edit_status_amount_description')"
                            @change="onItemUpdate(item.id, { category: $event })" />
                    </td>
                    <td :class="`status-${item.status}`">
                        <template v-if="noEdit">
                            {{ getStatusName(item.status) }}
                        </template>

                        <v-select v-else class="status-select" item-text="name" item-value="id" dense hide-details
                            :items="checkStatuses(statuses, item)" :value="item.status"
                            :disabled="!$can('appeals_edit_status_amount_description')"
                            @change="onItemUpdate(item.id, { status: $event })" />
                    </td>
                    <td>
                        {{ item.appeal || '-' }}
                    </td>
                    <td>
                        {{ item.response || '-' }}
                    </td>
                    <td>
                        {{ item.deadline || '-' }}
                    </td>
                </tr>
            </template>

            <template v-if="lastPage > 1" #footer>
                <v-divider />
                <div class="pt-1 pb-2">
                    <v-pagination color="warning" :value="currentPage" :length="lastPage" dark circle
                        @input="fetchList({ ...filters, page: $event })" />
                </div>
            </template>
        </v-data-table>

        <table-edit-dialog v-model="edit.dialog" :option="edit.option" :current-value="edit.value" :item-id="edit.itemId"
            @saved="onItemSaved" />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import DriverSelect from '@/components/shared/DriverSelect.vue';
import AutoSelect from '@/components/shared/AutoSelect.vue';
import ViolationDatePicker from '@/components/fines/ViolationDatePicker.vue';
import TableEditDialog from '@/components/fines/TableEditDialog.vue';
import formatAmount from '@/helpers/formatAmount';

export default {
    props: {
        updated: Boolean,
        noEdit: Boolean,
        carId: [String, Number],
        driverId: [String, Number],
    },
    components: {
        DriverSelect,
        AutoSelect,
        ViolationDatePicker,
        TableEditDialog,
    },
    data: () => ({
        loading: false,
        menuDate: false,
        statusesLoading: false,
        categoriesLoading: false,
        empoyeesLoading: false,
        itemsUpdating: [],
        searchString: '',
        filters: {
            search: '',
            dates: [],
            status: '',
            category: '',
        },
        edit: {
            dialog: false,
            option: '',
            itemId: '',
            value: null,
        },
    }),
    watch: {
        filters: {
            immediate: true,
            deep: true,
            handler(params) {
                if (params.dates.length === 1) return;
                this.fetchList(params);
            },
        },
        updated(value) {
            if (!value) return;
            this.updateList();
        },
    },
    computed: {
        ...mapState({
            list: ({ appeals }) => appeals.list,
            currentPage: ({ appeals }) => appeals.currentPage,
            lastPage: ({ appeals }) => appeals.lastPage,
            perPage: ({ appeals }) => appeals.perPage,
            statuses: ({ appeals }) => appeals.statuses,
            categories: ({ appeals }) => appeals.categories,
            employees: ({ appeals }) => appeals.employees,
        }),
        headers() {
            return [
                { text: this.$t('common.number'), sortable: false },
                { text: this.$t('common.table.created'), sortable: false },
                { text: this.$t('auto.fields.number'), sortable: false },
                { text: this.$t('workersList.investorFullName'), sortable: false },
                { text: this.$t('common.table.responsible'), sortable: false },
                { text: this.$t('appeals.category'), sortable: false },
                { text: this.$t('common.fields.status'), sortable: false },
                { text: this.$t('appeals.essence_appeal'), sortable: false },
                { text: this.$t('appeals.response'), sortable: false },
                { text: this.$t('appeals.deadlines'), sortable: false },
            ];
        },
    },
    methods: {
        formatAmount,
        changeDate(val) {
            if (val.length === 2) {
                this.$refs.menuDate.save(this.filters.dates);
            }
        },
        allowedDates(value) {
            const [from, to] = this.filters.dates;

            if (!from) return true;
            if (to) return true;

            const firstDate = new Date(from);
            const nextDate = new Date(value);

            return nextDate >= firstDate;
        },
        async fetchList(params) {
            this.loading = true;
            await this.$store.dispatch('appeals/fetchList', {
                ...params,
                car_id: this.carId,
                driver_id: this.driverId,
            });
            this.loading = false;
        },
        async updateList() {
            await this.fetchList({ ...this.filters, page: this.currentPage });
            this.$emit('updated');
        },
        onEdit(item, option) {
            this.edit.dialog = true;
            this.edit.value = item[option];
            this.edit.itemId = item.id;
            this.edit.option = option;
        },
        async onItemUpdate(id, params) {
            this.itemsUpdating.push(id);

            const result = await this.$store.dispatch('appeals/updateItem', {
                id,
                params,
            });

            this.onItemSaved(result);
            this.itemsUpdating = this.itemsUpdating.filter((item) => item !== id);
        },
        onItemSaved({ success }) {
            if (success) {
                this.updateList();
                this.edit.dialog = false;
            } else {
                this.$emit('update-error');
            }
        },
        getStatusName(id) {
            const status = this.statuses.find((item) => item.id === id);
            return status ? status.name : '-';
        },
        getCategoryName(id) {
            const category = this.categories.find((item) => item.id === id);
            return category ? category.name : '-';
        },

        checkStatuses(statuses, item) {
            let newStatuses = JSON.parse(JSON.stringify(statuses));
            for (const [key, value] of Object.entries(newStatuses)) {
                if ((value.id == 3 || value.id == 5) && !item.response) {
                    value.disabled = true;
                }
            }
            return newStatuses;
        },
    },
    async created() {
        if (!this.statuses.length) {
            this.statusesLoading = true;
            await this.$store.dispatch('appeals/fetchStatuses');
            this.statusesLoading = false;
        }
        if (!this.categories.length) {
            this.categoriesLoading = true;
            await this.$store.dispatch('appeals/fetchCategories');
            this.categoriesLoading = false;
        }
        if (!this.employees.length) {
            this.employeesLoading = true;
            await this.$store.dispatch('appeals/fetchEmployees');
            this.employeesLoading = false;
        }
    },
    beforeDestroy() {
        this.$store.commit('appeals/setList', []);
    },
};
</script>

<style lang="scss">
.appeals-table {
    width: 100%;
    line-height: 1.3;

    &__search {
        max-width: 400px;
    }

    &__filter {
        max-width: 230px;

        &:not(:last-child) {
            margin-right: 35px;
        }
    }

    &__auto {
        min-width: 85px;
    }

    &__edit-btn {
        text-decoration: underline;
        font-weight: 500;

        &:hover {
            text-decoration: none;
        }

        &[disabled] {
            text-decoration: none;
            font-weight: normal;
        }
    }

    .status-select {
        width: 150px;

        .v-select__selection {
            font-size: 12px;
            margin-top: 2px;
            margin-bottom: 2px;
        }
    }

    .updating {
        pointer-events: none;
        opacity: 0.5;
    }

    .status-0,
    .status-4 {
        background-color: #ffc4c4;
    }

    .status-1,
    .status-2 {
        background-color: #fff79b;
    }

    .status-3 {
        background-color: #c6f9c0;
    }

    .no-wrap {
        white-space: nowrap;
    }
}

.v-pagination__item,
.v-pagination__navigation {
    box-shadow: none;
}
</style>
